import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Card, Button, Alert, Row, Col, Spinner } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Form,
  Select,
  Table,
  InputNumber,
  DatePicker,
  Input,
  Checkbox,
  TableColumnsType,
  Space,
  Tag,
  Empty,
  Alert as AntAlert,
  Descriptions,
  Divider,
  Modal,
  Upload,
  Button as AntButton,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  SaveOutlined,
  FileTextOutlined,
  UploadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import {
  PaymentData,
  PaymentType,
  PaymentMethod,
  PaymentStatus,
  UserRole,
  ApprovedRaBillData,
  PaymentAllocation,
  PaymentDeduction,
} from "./types";
import {
  mockApprovedRaBillsData,
  getEligibleRaBillsForPayment,
  mockContractorOptions,
} from "./mockData/approvedRaBillsData";
import {
  mockLegalEntityOptions,
  mockBankAccountOptions,
  mockTdsSectionOptions,
  mockPaymentMethodOptions,
} from "./mockData/paymentData";

const { Option } = Select;
const { TextArea } = Input;

interface FormData {
  paymentVoucherNumber: string;
  paymentDate: dayjs.Dayjs | null;
  paymentType: PaymentType;
  contractorId: string;
  legalEntityId: string;
  paymentMethod: PaymentMethod;
  bankAccountId: string;
  tdsSection: string;
  tdsRate: number;
  narration: string;
  allocations: PaymentAllocation[];
}

const CreatePayment: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const duplicateId = searchParams.get("duplicate");
  const isEdit = Boolean(editId);
  const isDuplicate = Boolean(duplicateId);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedRaBills, setSelectedRaBills] = useState<ApprovedRaBillData[]>(
    []
  );
  const [eligibleRaBills, setEligibleRaBills] = useState<ApprovedRaBillData[]>(
    []
  );
  const [showEligibleBills, setShowEligibleBills] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState<string>("");

  // Commercial calculations
  const [sumOfAllocations, setSumOfAllocations] = useState(0);
  const [tdsThisPayment, setTdsThisPayment] = useState(0);
  const [otherAdjustments, setOtherAdjustments] = useState(0);
  const [netPayable, setNetPayable] = useState(0);

  useEffect(() => {
    loadEligibleRaBills();
    initializeForm();
  }, []);

  const loadEligibleRaBills = () => {
    const eligible = getEligibleRaBillsForPayment();
    setEligibleRaBills(eligible);
  };

  const initializeForm = () => {
    // Set default values
    form.setFieldsValue({
      paymentVoucherNumber: generatePaymentVoucherNumber(),
      paymentDate: dayjs(),
      paymentType: PaymentType.RA,
      tdsRate: 1.0,
    });
  };

  const generatePaymentVoucherNumber = () => {
    const currentDate = dayjs();
    const year = currentDate.year();
    const month = currentDate.format("MM");
    const randomNum = Math.floor(Math.random() * 9999)
      .toString()
      .padStart(4, "0");
    return `PAY/${year}/HYD/${randomNum}`;
  };

  const handleContractorChange = (contractorId: string) => {
    setSelectedContractor(contractorId);

    // Filter RA bills for selected contractor
    const contractorBills = eligibleRaBills.filter(
      (bill) => bill.contractorId === contractorId
    );

    // Clear existing selections if contractor changes
    setSelectedRaBills([]);
    form.setFieldsValue({ allocations: [] });
    calculateTotals([]);
  };

  const showRaBillSelection = () => {
    if (!selectedContractor) {
      Swal.fire("Warning", "Please select a contractor first", "warning");
      return;
    }
    setShowEligibleBills(true);
  };

  const handleRaBillSelection = (
    selectedRowKeys: React.Key[],
    selectedRows: ApprovedRaBillData[]
  ) => {
    setSelectedRaBills(selectedRows);

    // Create allocations for selected bills
    const allocations: PaymentAllocation[] = selectedRows.map(
      (bill, index) => ({
        id: `ALLOC${String(index + 1).padStart(3, "0")}`,
        raBillId: bill.id,
        raBillNumber: bill.raBillNumber,
        workOrderId: bill.workOrderId,
        workOrderNumber: bill.workOrderNumber,
        raPeriod: bill.raPeriod,
        raBillValue: bill.raBillValue,
        alreadyPaid: bill.alreadyPaid,
        billBalance: bill.balance,
        thisPaymentAmount: bill.balance, // Default to full balance
        resultingStatus: "Paid" as const,
      })
    );

    form.setFieldsValue({ allocations });
    calculateTotals(allocations);
    setShowEligibleBills(false);
  };

  const calculateTotals = (allocations: PaymentAllocation[]) => {
    const sum = allocations.reduce(
      (total, allocation) => total + allocation.thisPaymentAmount,
      0
    );
    setSumOfAllocations(sum);

    const tdsRate = form.getFieldValue("tdsRate") || 1.0;
    const tds = (sum * tdsRate) / 100;
    setTdsThisPayment(tds);

    const net = sum - tds - otherAdjustments;
    setNetPayable(net);
  };

  const handleAllocationChange = (
    index: number,
    field: string,
    value: number
  ) => {
    const allocations = form.getFieldValue("allocations") || [];
    allocations[index] = { ...allocations[index], [field]: value };

    // Update resulting status
    if (field === "thisPaymentAmount") {
      const allocation = allocations[index];
      if (value >= allocation.billBalance) {
        allocation.resultingStatus = "Paid";
      } else if (value > 0) {
        allocation.resultingStatus = "Partially Paid";
      } else {
        allocation.resultingStatus = "Unpaid";
      }
    }

    form.setFieldsValue({ allocations });
    calculateTotals(allocations);
  };

  const removeAllocation = (index: number) => {
    const allocations = form.getFieldValue("allocations") || [];
    const updatedAllocations = allocations.filter(
      (_: any, i: number) => i !== index
    );
    form.setFieldsValue({ allocations: updatedAllocations });

    // Update selected RA bills
    const updatedSelectedBills = selectedRaBills.filter((_, i) => i !== index);
    setSelectedRaBills(updatedSelectedBills);

    calculateTotals(updatedAllocations);
  };

  const handleTdsRateChange = (value: number | null) => {
    if (value !== null) {
      const allocations = form.getFieldValue("allocations") || [];
      calculateTotals(allocations);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Create payment data
      const paymentData: Partial<PaymentData> = {
        ...values,
        paymentDate: values.paymentDate?.format("YYYY-MM-DD"),
        status: PaymentStatus.Draft,
        sumOfAllocations,
        tdsThisPayment,
        otherAdjustments,
        netPayable,
        createdAt: dayjs().format("YYYY-MM-DD"),
        createdBy: "current.user@company.com", // Mock current user
        hasSoftHold: false,
        hasHardHold: false,
        deductions: [
          {
            id: "DED001",
            deductionType: "TDS" as const,
            description: `TDS under section ${values.tdsSection}`,
            basis: `Pre-GST taxable base × ${values.tdsRate}%`,
            amount: tdsThisPayment,
          },
        ],
        attachments: [],
        approvalHistory: [
          {
            action: "Created",
            actionBy: "current.user@company.com",
            actionDate: dayjs().toISOString(),
            role: UserRole.Maker,
            status: PaymentStatus.Draft,
          },
        ],
      };

      console.log("Payment created:", paymentData);

      Swal.fire("Success!", "Payment created successfully", "success").then(
        () => {
          navigate("/payments/view-payments");
        }
      );
    } catch (error) {
      console.error("Form validation failed:", error);
      Swal.fire("Error!", "Please fill all required fields", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsDraft = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      Swal.fire("Success!", "Payment saved as draft", "success").then(() => {
        navigate("/payments/view-payments");
      });
    } catch (error) {
      console.error("Error saving draft:", error);
    } finally {
      setLoading(false);
    }
  };

  // RA Bills Selection Columns
  const raBillColumns: TableColumnsType<ApprovedRaBillData> = [
    {
      title: "RA Bill Number",
      dataIndex: "raBillNumber",
      key: "raBillNumber",
      width: 140,
    },
    {
      title: "Work Order",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 140,
    },
    {
      title: "Site/Project",
      key: "siteProject",
      width: 200,
      render: (record: ApprovedRaBillData) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          {record.projectName && (
            <small className="text-muted">{record.projectName}</small>
          )}
        </div>
      ),
    },
    {
      title: "RA Period",
      dataIndex: "raPeriod",
      key: "raPeriod",
      width: 100,
    },
    {
      title: "Balance Amount",
      dataIndex: "balance",
      key: "balance",
      width: 120,
      render: (amount: number) => `₹${amount?.toLocaleString()}`,
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (record: ApprovedRaBillData) => (
        <div>
          <Tag color={record.status === "Approved" ? "green" : "blue"}>
            {record.status}
          </Tag>
          {record.hasSoftHold && <Tag color="orange">Soft Hold</Tag>}
        </div>
      ),
    },
  ];

  // Payment Allocations Columns
  const allocationColumns: TableColumnsType<any> = [
    {
      title: "#",
      key: "#",
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "RA Bill Number",
      dataIndex: "raBillNumber",
      key: "raBillNumber",
      width: 140,
    },
    {
      title: "Work Order",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 140,
    },
    {
      title: "RA Period",
      dataIndex: "raPeriod",
      key: "raPeriod",
      width: 100,
    },
    {
      title: "Bill Value",
      dataIndex: "raBillValue",
      key: "raBillValue",
      width: 120,
      render: (amount: number) => `₹${amount?.toLocaleString()}`,
    },
    {
      title: "Already Paid",
      dataIndex: "alreadyPaid",
      key: "alreadyPaid",
      width: 120,
      render: (amount: number) => `₹${amount?.toLocaleString()}`,
    },
    {
      title: "Balance",
      dataIndex: "billBalance",
      key: "billBalance",
      width: 120,
      render: (amount: number) => `₹${amount?.toLocaleString()}`,
    },
    {
      title: "This Payment",
      dataIndex: "thisPaymentAmount",
      key: "thisPaymentAmount",
      width: 140,
      render: (amount: number, _: any, index: number) => (
        <InputNumber
          value={amount}
          min={0}
          max={form.getFieldValue("allocations")?.[index]?.billBalance || 0}
          formatter={(value) =>
            `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
          parser={(value) => value!.replace(/₹\s?|(,*)/g, "") as any}
          onChange={(value) =>
            handleAllocationChange(index, "thisPaymentAmount", value || 0)
          }
          style={{ width: "100%" }}
        />
      ),
    },
    // {
    //   title: "Resulting Status",
    //   dataIndex: "resultingStatus",
    //   key: "resultingStatus",
    //   width: 120,
    //   render: (status: string) => {
    //     const color =
    //       status === "Paid"
    //         ? "green"
    //         : status === "Partially Paid"
    //         ? "orange"
    //         : "red";
    //     return <Tag color={color}>{status}</Tag>;
    //   },
    // },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_: any, __: any, index: number) => (
        <AntButton
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeAllocation(index)}
        />
      ),
    },
  ];

  return (
    <>
      <Head title="Create Payment" />
      <Content>
        <Card className="card-bordered">
          <div className="card-inner card-inner-lg">
            <BlockHead size="lg">
              <BlockBetween>
                <BlockHeadContent>
                  <BlockTitle tag="h4">
                    {isEdit
                      ? "Edit Payment"
                      : isDuplicate
                      ? "Duplicate Payment"
                      : "Create Payment"}
                  </BlockTitle>
                </BlockHeadContent>
                <div className="d-flex">
                  <div className="actions clearfix">
                    <ul
                      className="m-auto d-flex align-items-center justify-content-center p-0"
                      style={{ flexDirection: "row-reverse" }}
                    >
                      <li className="pt-0 pb-0">
                        <Button
                          className="toggle"
                          color="primary"
                          onClick={() => navigate(-1)}
                        >
                          <Icon name="arrow-left" />
                          <span>Back</span>
                        </Button>
                      </li>
                    </ul>
                  </div>
                </div>
              </BlockBetween>
            </BlockHead>

            <div className="content clearfix">
              <Form form={form} layout="vertical" size="large">
                {/* Basic Information */}
                <Block>
                  {/* <h5>Basic Information</h5> */}
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Form.Item
                        name="paymentVoucherNumber"
                        label="Payment Voucher Number"
                        rules={[
                          {
                            required: true,
                            message: "Payment voucher number is required",
                          },
                        ]}
                      >
                        <Input disabled />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="paymentDate"
                        label="Payment Date"
                        rules={[
                          {
                            required: true,
                            message: "Payment date is required",
                          },
                        ]}
                      >
                        <DatePicker style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="paymentType"
                        label="Payment Type"
                        rules={[
                          {
                            required: true,
                            message: "Payment type is required",
                          },
                        ]}
                      >
                        <Select>
                          <Option value={PaymentType.RA}>RA Payment</Option>
                          <Option value={PaymentType.RetentionRelease}>
                            Release Retaintion Amount
                          </Option>
                          <Option value={PaymentType.OnHold}>
                            Release OnHold Amount
                          </Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item
                        name="contractorId"
                        label="Contractor"
                        rules={[
                          { required: true, message: "Contractor is required" },
                        ]}
                      >
                        <Select
                          placeholder="Select contractor"
                          showSearch
                          optionFilterProp="children"
                          onChange={handleContractorChange}
                        >
                          {mockContractorOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="legalEntityId"
                        label="Legal Entity"
                        rules={[
                          {
                            required: true,
                            message: "Legal entity is required",
                          },
                        ]}
                      >
                        <Select placeholder="Select legal entity">
                          {mockLegalEntityOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Form.Item
                        name="paymentMethod"
                        label="Payment Method"
                        rules={[
                          {
                            required: true,
                            message: "Payment method is required",
                          },
                        ]}
                      >
                        <Select placeholder="Select payment method">
                          {mockPaymentMethodOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="bankAccountId"
                        label="Bank Account"
                        rules={[
                          {
                            required: true,
                            message: "Bank account is required",
                          },
                        ]}
                      >
                        <Select placeholder="Select bank account">
                          {mockBankAccountOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    {/* <Col span={8}>
                      <Form.Item
                        name="tdsSection"
                        label="TDS Section"
                        rules={[{ required: true, message: "TDS section is required" }]}
                      >
                        <Select placeholder="Select TDS section">
                          {mockTdsSectionOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col> */}
                  </Row>

                  <Row gutter={[16, 16]}>
                    {/* <Col span={6}>
                      <Form.Item
                        name="tdsRate"
                        label="TDS Rate (%)"
                        rules={[{ required: true, message: "TDS rate is required" }]}
                      >
                        <InputNumber
                          min={0}
                          max={100}
                          step={0.1}
                          style={{ width: "100%" }}
                          onChange={handleTdsRateChange}
                        />
                      </Form.Item>
                    </Col> */}
                    <Col span={18}>
                      <Form.Item name="narration" label="Narration">
                        <TextArea
                          rows={2}
                          placeholder="Enter payment narration"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Block>

                <Divider />

                {/* RA Bill Selection */}
                <Block>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>Payment Allocations</h5>
                    <AntButton
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={showRaBillSelection}
                      disabled={!selectedContractor}
                    >
                      Add RA Bills
                    </AntButton>
                  </div>

                  <Form.Item name="allocations">
                    {selectedRaBills.length === 0 ? (
                      <div className="text-center py-4">
                        <Empty description="No RA bills selected. Click 'Add RA Bills' to select bills for payment." />
                      </div>
                    ) : (
                      <Table
                        columns={allocationColumns}
                        dataSource={form.getFieldValue("allocations") || []}
                        rowKey="id"
                        scroll={{ x: 1200 }}
                        bordered
                        size="small"
                        pagination={false}
                      />
                    )}
                  </Form.Item>
                </Block>

                <Divider />

                {/* Commercial Summary */}
                <Block>
                  <h5> Commercial Summary</h5>
                  <Row>
                    <Col span={12}>
                      <Descriptions bordered size="small" column={1}>
                        <Descriptions.Item label="Sum of Allocations">
                          <strong>₹{sumOfAllocations.toLocaleString()}</strong>
                        </Descriptions.Item>
                        <Descriptions.Item label="TDS This Payment">
                          <strong className="text-warning">
                            -₹{tdsThisPayment.toLocaleString()}
                          </strong>
                        </Descriptions.Item>
                        <Descriptions.Item label="Other Adjustments">
                          <strong>₹{otherAdjustments.toLocaleString()}</strong>
                        </Descriptions.Item>
                        <Descriptions.Item label="Net Payable">
                          <strong className="text-success">
                            ₹{netPayable.toLocaleString()}
                          </strong>
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                  </Row>
                </Block>

                <Divider />

                {/* Action Buttons */}
                <div className="d-flex justify-content-end">
                  <Space>
                    <Button color="secondary" onClick={() => navigate(-1)}>
                      Cancel
                    </Button>
                    <Button
                      color="warning"
                      onClick={handleSaveAsDraft}
                      disabled={loading}
                    >
                      {loading ? <Spinner size="sm" /> : <SaveOutlined />}
                      Save as Draft
                    </Button>
                    <Button
                      color="success"
                      onClick={handleSubmit}
                      disabled={loading || selectedRaBills.length === 0}
                    >
                      {loading ? <Spinner size="sm" /> : <FileTextOutlined />}
                      Submit for Approval
                    </Button>
                  </Space>
                </div>
              </Form>
            </div>
          </div>
        </Card>

        {/* RA Bills Selection Modal */}
        <Modal
          title={`Select RA Bills for Payment - ${
            mockContractorOptions.find((c) => c.value === selectedContractor)
              ?.label || ""
          }`}
          open={showEligibleBills}
          onCancel={() => setShowEligibleBills(false)}
          width={1200}
          footer={null}
        >
          <Table
            columns={raBillColumns}
            dataSource={eligibleRaBills.filter(
              (bill) => bill.contractorId === selectedContractor
            )}
            rowKey="id"
            rowSelection={{
              type: "checkbox",
              selectedRowKeys: selectedRaBills.map((bill) => bill.id),
              onSelect: (record, selected) => {
                if (selected) {
                  handleRaBillSelection(
                    [...selectedRaBills.map((b) => b.id), record.id],
                    [...selectedRaBills, record]
                  );
                } else {
                  const filtered = selectedRaBills.filter(
                    (bill) => bill.id !== record.id
                  );
                  handleRaBillSelection(
                    filtered.map((b) => b.id),
                    filtered
                  );
                }
              },
              onSelectAll: (selected, selectedRows, changeRows) => {
                if (selected) {
                  const allBills = eligibleRaBills.filter(
                    (bill) => bill.contractorId === selectedContractor
                  );
                  handleRaBillSelection(
                    allBills.map((b) => b.id),
                    allBills
                  );
                } else {
                  handleRaBillSelection([], []);
                }
              },
            }}
            scroll={{ x: 800 }}
            bordered
            size="small"
            pagination={false}
          />
        </Modal>
      </Content>
    </>
  );
};

export default CreatePayment;
